import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, XCircle, RotateCcw, Clock, Send, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { approvalWorkflowAPI } from '../../services/api';

const statusConfig = {
  Pending: { color: '#E65100', bg: '#FFF3E0', icon: Clock, label: 'Pending Approval' },
  'In Progress': { color: '#1565C0', bg: '#E3F2FD', icon: Shield, label: 'In Progress' },
  Approved: { color: '#2E7D32', bg: '#E8F5E9', icon: CheckCircle, label: 'Approved' },
  Rejected: { color: '#C62828', bg: '#FFEBEE', icon: XCircle, label: 'Rejected' },
  Returned: { color: '#F57F17', bg: '#FFFDE7', icon: RotateCcw, label: 'Returned for Revision' },
};

const ApprovalStatusBadge = ({ documentType, documentId, documentNumber, onStatusChange }) => {
  const [checkResult, setCheckResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [acting, setActing] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [comment, setComment] = useState('');
  const [showActions, setShowActions] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('prismoline_user') || '{}');

  useEffect(() => {
    if (documentId) fetchStatus();
  }, [documentId]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await approvalWorkflowAPI.checkStatus(documentType, documentId);
      setCheckResult(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await approvalWorkflowAPI.submitForApproval({
        document_type: documentType,
        document_id: documentId,
        document_number: documentNumber,
      });
      fetchStatus();
      if (onStatusChange) onStatusChange();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const handleAction = async (action) => {
    setActing(true);
    try {
      const reqId = checkResult?.approval_request?.id;
      await approvalWorkflowAPI.takeAction(reqId, { action, comment });
      setComment('');
      setShowActions(false);
      fetchStatus();
      if (onStatusChange) onStatusChange();
    } catch (err) { alert(err.response?.data?.detail || 'Action failed'); }
    finally { setActing(false); }
  };

  if (loading) return <div className="flex items-center gap-1 text-[10px] text-gray-400"><Loader2 className="w-3 h-3 animate-spin" /> Checking approval...</div>;
  if (!checkResult || !checkResult.needs_approval) return null;

  const approvalStatus = checkResult.approval_status;
  const request = checkResult.approval_request;
  const config = statusConfig[approvalStatus] || statusConfig.Pending;
  const StatusIcon = config.icon;

  // Determine if current user can act
  let canAct = false;
  if (request && ['Pending', 'In Progress'].includes(request.status)) {
    const currentLevel = request.current_level;
    const chain = request.approval_chain || [];
    const levelConfig = chain.find(l => l.level === currentLevel);
    if (levelConfig && (levelConfig.role === currentUser.role || currentUser.role === 'Admin')) {
      canAct = true;
    }
  }

  return (
    <div className="border rounded-xl overflow-hidden" style={{ borderColor: config.color + '30' }}>
      {/* Status Header */}
      <div className="flex items-center justify-between px-3 py-2" style={{ backgroundColor: config.bg }}>
        <div className="flex items-center gap-2">
          <StatusIcon className="w-4 h-4" style={{ color: config.color }} />
          <span className="text-[11px] font-bold" style={{ color: config.color }}>{config.label}</span>
          {request && (
            <span className="text-[10px] text-gray-500">
              Level {request.current_level}/{request.total_levels}
              {request.workflow_name && ` — ${request.workflow_name}`}
            </span>
          )}
        </div>
        {!approvalStatus && (
          <button onClick={handleSubmit} disabled={submitting} data-testid="submit-for-approval-btn"
            className="flex items-center gap-1 px-3 py-1 text-[10px] font-bold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Submit for Approval
          </button>
        )}
      </div>

      {/* Approval Chain Progress */}
      {request && request.approval_chain && (
        <div className="px-3 py-2 flex items-center gap-1">
          {request.approval_chain.map((level, idx) => {
            const isCompleted = request.logs?.some(l => l.level === level.level && l.action === 'approved');
            const isCurrent = level.level === request.current_level && ['Pending', 'In Progress'].includes(request.status);
            const isRejected = request.logs?.some(l => l.level === level.level && (l.action === 'rejected' || l.action === 'returned'));
            return (
              <React.Fragment key={idx}>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${
                  isCompleted ? 'bg-emerald-100 text-emerald-700' :
                  isRejected ? 'bg-red-100 text-red-700' :
                  isCurrent ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' :
                  'bg-gray-100 text-gray-500'
                }`}>
                  {isCompleted ? <CheckCircle className="w-3 h-3" /> : isRejected ? <XCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                  {level.label}
                </div>
                {idx < request.approval_chain.length - 1 && <span className="text-gray-300 text-[10px]">→</span>}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Action Buttons for Approver */}
      {canAct && (
        <div className="px-3 py-2 border-t border-gray-100">
          {!showActions ? (
            <button onClick={() => setShowActions(true)} data-testid="show-approval-actions-btn"
              className="text-[11px] font-semibold text-blue-600 hover:text-blue-800">
              Take Action...
            </button>
          ) : (
            <div className="space-y-2">
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2}
                placeholder="Add a comment (optional)..."
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              <div className="flex gap-2">
                <button onClick={() => handleAction('approve')} disabled={acting} data-testid="approve-btn"
                  className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700">
                  <CheckCircle className="w-3 h-3" /> Approve
                </button>
                <button onClick={() => handleAction('return')} disabled={acting} data-testid="return-btn"
                  className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200">
                  <RotateCcw className="w-3 h-3" /> Return for Revision
                </button>
                <button onClick={() => handleAction('reject')} disabled={acting} data-testid="reject-btn"
                  className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold text-red-700 bg-red-100 rounded-lg hover:bg-red-200">
                  <XCircle className="w-3 h-3" /> Reject
                </button>
                <button onClick={() => setShowActions(false)} className="px-2 py-1.5 text-[10px] text-gray-500 hover:text-gray-700">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Approval Logs */}
      {request && request.logs && request.logs.length > 0 && (
        <div className="border-t border-gray-100">
          <button onClick={() => setShowLogs(!showLogs)} className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold text-gray-500 hover:bg-gray-50">
            Approval Log ({request.logs.length} entries)
            {showLogs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showLogs && (
            <div className="px-3 pb-2 space-y-1">
              {request.logs.map((log, idx) => (
                <div key={idx} className="flex items-start gap-2 text-[10px]">
                  <span className="text-gray-400 flex-shrink-0 w-24">{new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  <span className={`font-semibold flex-shrink-0 ${
                    log.action === 'approved' ? 'text-emerald-600' : log.action === 'rejected' ? 'text-red-600' : log.action === 'returned' ? 'text-amber-600' : 'text-blue-600'
                  }`}>{log.action.toUpperCase()}</span>
                  <span className="text-gray-600">{log.user_name} ({log.role})</span>
                  {log.comment && <span className="text-gray-400 italic">— {log.comment}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApprovalStatusBadge;
